import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ConnectButton,
  useActiveAccount,
  useDisconnect,
  useActiveWallet,
  useActiveWalletChain,
} from "thirdweb/react";
import { createThirdwebClient } from "thirdweb";
import { createWallet } from "thirdweb/wallets";
import { NobodySausageGlyphConnectButton } from "./NobodySausageGlyphConnectButton";
import { useNobodySausageGlyph } from "../contexts/NobodySausageGlyphContext";
import { useRouteChain, useRouteWalletConfig } from "../contexts/ThirdwebProvider";
import { logger } from "../utils/logger";
import { inMetamaskBrowser, openInMetamask } from "../utils/metamask";

function normalizeChainId(x?: string | number | bigint): number | undefined {
  if (x === undefined) return undefined;
  if (typeof x === "string") {
    if (x.startsWith("eip155:")) return parseInt(x.split(":")[1], 10);
    if (x.startsWith("0x")) return parseInt(x, 16);
    const n = parseInt(x, 10);
    return Number.isNaN(n) ? undefined : n;
  }
  return Number(x);
}

interface ThirdwebWalletConnectProps {
  className?: string;
  showDropdown?: boolean;
  onWalletDetailsClick?: () => void;
  btnTitle?: string;
}

export function ThirdwebWalletConnect({
  className = "",
  showDropdown = true,
  onWalletDetailsClick,
  btnTitle = "Continue",
}: ThirdwebWalletConnectProps) {
  const client = useMemo(() => {
    return createThirdwebClient({
      clientId: process.env.REACT_APP_THIRDWEB_CLIENT_ID as string,
    });
  }, []);

  // v5 wallet/account hooks
  const account = useActiveAccount();
  const wallet = useActiveWallet();
  const walletChain = useActiveWalletChain();
  const { disconnect: disconnectThirdweb } = useDisconnect();
  const {
    disconnect: disconnectGlyph,
    isConnected: isGlyphConnected,
  } = useNobodySausageGlyph();
  const eoaAccount = useMemo(() => {
    return (
      (wallet as any)?.getAdminAccount?.() ||
      (wallet as any)?.getAccount?.() ||
      null
    );
  }, [wallet]);
  const displayAddress = eoaAccount?.address || account?.address;

  // Local UI state (from previous implementation)
  const [showWalletDetails, setShowWalletDetails] = useState(false);
  const [showDropdownMenu, setShowDropdownMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showSignupPopup, setShowSignupPopup] = useState(false);
  const [showMetamaskRedirect, setShowMetamaskRedirect] = useState(false);

  const chainId = useMemo(() => {
    return normalizeChainId((walletChain as any)?.id);
  }, [walletChain]);

  const wcProjectId = process.env.REACT_APP_WC_PROJECT_ID;
  
  const routeChain = useRouteChain();
  const routeWalletConfig = useRouteWalletConfig();
  const { wallets: routeWallets, connectMode } = routeWalletConfig;
  const showNativeGlyphButton = connectMode === "native-glyph";

  // Create Metamask-only wallet list for when in Metamask browser
  const metamaskOnlyWallets = useMemo(() => {
    return [createWallet("io.metamask")];
  }, []);

  // Log wallet connection status changes
  useEffect(() => {
    if (displayAddress) {
      logger.wallet(`Wallet connected`, {
        address: displayAddress,
        chainId,
        formattedAddress: formatAddress(displayAddress),
      });
    } else {
      logger.wallet(`Wallet disconnected`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayAddress, chainId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdownMenu(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleConnectMetamask = () => {
    setShowSignupPopup(false);
    setShowMetamaskRedirect(true);
  };

  const handleRedirectToMetamask = () => {
    try {
      openInMetamask();
    } catch {
      // ignore
    }
  };

  const formatAddress = (addr?: string) => {
    if (!addr) return "";
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const copyAddress = async () => {
    if (displayAddress) {
      try {
        logger.wallet(`Copying address to clipboard`, {
          address: formatAddress(displayAddress),
        });
        await navigator.clipboard.writeText(displayAddress);
        setCopied(true);
        logger.wallet(`Address copied successfully`);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        logger.walletError("Failed to copy address", err as Error, {
          address: formatAddress(displayAddress),
        });
      }
    }
  };

  const handleDisconnect = async () => {
    try {
      logger.wallet(`Disconnecting wallet`, {
        address: formatAddress(displayAddress),
      });
      // Prefer wallet instance method if available
      if (isGlyphConnected) {
        disconnectGlyph();
      } else if ((wallet as any)?.disconnect) {
        await (wallet as any).disconnect();
      } else if ((wallet as any)?.disconnect?.disconnect) {
        // fallback weird shapes
        await (wallet as any).disconnect.disconnect();
      } else if (wallet) {
        disconnectThirdweb(wallet);
      }
      setShowWalletDetails(false);
      setShowDropdownMenu(false);
      logger.wallet(`Wallet disconnected successfully`);
    } catch (error) {
      logger.walletError("Failed to disconnect wallet", error as Error, {
        address: formatAddress(displayAddress),
      });
    }
  };

  // If not connected: show conditional UI based on MetaMask in-app browser
  if (!displayAddress) {
    const isMMInApp = inMetamaskBrowser();

    if (isMMInApp) {
      // In MetaMask in-app browser: show ONLY Metamask wallet (no web2 login)
      return (
        <div className={className}>
          <ConnectButton
            client={client}
            wallets={metamaskOnlyWallets}
            chain={routeChain}
            chains={[routeChain]}
            walletConnect={{
              projectId: wcProjectId,
            }}
            appMetadata={{
              name: "223",
              url: typeof window !== "undefined" ? window.location.origin : "",
              description: "223 dapp",
              logoUrl:
                typeof window !== "undefined"
                  ? `${window.location.origin}/223-logo.png`
                  : "/223-logo.png",
            }}
            theme="light"
            accountAbstraction={{
              chain: routeChain,
              sponsorGas: true,
            }}
            connectButton={{
              label: btnTitle,
              style: {
                backgroundColor: "transparent",
                color: "black",
                border: "2px solid black",
                padding: "2px 10px",
                fontSize: "15px",
                fontWeight: "bold",
                fontFamily: "inherit",
                lineHeight: "1",
                borderRadius: "0px",
                minWidth: "fit-content",
                maxHeight: "34px",
              },
            }}
            connectModal={{
              title: "Connect to 223",
              size: "compact",
              showThirdwebBranding: false,
              welcomeScreen: {
                title: "Welcome to 223",
                subtitle:
                  "Connect your wallet or sign in with social media to get started",
              },
            }}
          />
        </div>
      );
    }

    // Different browser: show single "Sign up" button that opens popup
    return (
      <>
        {/* Hidden ConnectButton for auto-restoring sessions */}
        <div style={{ display: "none" }}>
          <ConnectButton
            client={client}
            wallets={routeWallets}
            chain={routeChain}
            chains={[routeChain]}
            appMetadata={{
              name: "223",
              url: typeof window !== "undefined" ? window.location.origin : "",
              description: "223 dapp",
              logoUrl:
                typeof window !== "undefined"
                  ? `${window.location.origin}/223-logo.png`
                  : "/223-logo.png",
            }}
            theme="light"
            accountAbstraction={{
              chain: routeChain,
              sponsorGas: true,
            }}
          />
        </div>

        <div className={className}>
          <button
            onClick={() => setShowSignupPopup(true)}
            style={{
              backgroundColor: "transparent",
              color: "black",
              border: "2px solid black",
              padding: "2px 10px",
              fontWeight: "bold",
              fontFamily: "inherit",
              lineHeight: "1",
              borderRadius: "0px",
              minWidth: "fit-content",
              minHeight: "35px",
            }}
          >
            {btnTitle}
          </button>
        </div>

        {/* Sign up popup with two options */}
        {showSignupPopup && (
          <div className="flex fixed inset-0 z-50 justify-center items-center px-[22px] bg-black bg-opacity-50 !m-0">
            <div className="p-4 w-full max-w-md bg-white">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-lg font-bold">Sign up / Log in</h2>
                <button
                  onClick={() => setShowSignupPopup(false)}
                  className="text-2xl leading-none text-gray-400 hover:text-gray-600"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              <div className="flex flex-col space-y-3">
                <button
                  onClick={handleConnectMetamask}
                  style={{
                    backgroundColor: "transparent",
                    color: "black",
                    border: "2px solid black",
                    padding: "8px 16px",
                    fontSize: "15px",
                    fontWeight: "bold",
                    fontFamily: "inherit",
                    lineHeight: "1",
                    borderRadius: "0px",
                    width: "100%",
                  }}
                >
                  Connect Metamask wallet
                </button>

                {showNativeGlyphButton && (
                  <div style={{ width: "100%" }}>
                    <NobodySausageGlyphConnectButton
                      onConnect={() => {
                        setShowSignupPopup(false);
                      }}
                    />
                  </div>
                )}

                <div style={{ width: "100%" }}>
                  <ConnectButton
                    client={client}
                    wallets={routeWallets}
                    chain={routeChain}
                    chains={[routeChain]}
                    appMetadata={{
                      name: "223",
                      url:
                        typeof window !== "undefined"
                          ? window.location.origin
                          : "",
                      description: "223 dapp",
                      logoUrl:
                        typeof window !== "undefined"
                          ? `${window.location.origin}/223-logo.png`
                          : "/223-logo.png",
                    }}
                    theme="light"
                    accountAbstraction={{
                      chain: routeChain,
                      sponsorGas: true,
                    }}
                    connectButton={{
                      label: "Sign up",
                      style: {
                        backgroundColor: "transparent",
                        color: "black",
                        border: "2px solid black",
                        padding: "8px 16px",
                        fontSize: "15px",
                        height: "35px",
                        fontWeight: "bold",
                        fontFamily: "inherit",
                        lineHeight: "1",
                        borderRadius: "0px",
                        width: "100%",
                      },
                    }}
                    connectModal={{
                      title: "Connect to 223",
                      size: "compact",
                      showThirdwebBranding: false,
                      welcomeScreen: {
                        title: "Welcome to 223",
                        subtitle:
                          "Connect your wallet or sign in with social media to get started",
                      },
                    }}
                    onConnect={() => {
                      setShowSignupPopup(false);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MetaMask redirect popup */}
        {showMetamaskRedirect && (
          <div className="flex fixed inset-0 z-50 justify-center items-center px-[22px] bg-black bg-opacity-50">
            <div className="p-4 w-full max-w-md bg-white">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-lg font-bold">Open in MetaMask</h2>
                  <p className="mt-2 text-sm text-[#626262]">
                    This app is only optimized for Metamask browser. Click the
                    button below to be redirected to Metamask browser.
                  </p>
                </div>
                <button
                  onClick={() => setShowMetamaskRedirect(false)}
                  className="text-2xl leading-none text-gray-400 hover:text-gray-600"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              <div className="mt-4 flex space-x-2">
                <button
                  onClick={handleRedirectToMetamask}
                  className="px-4 py-2 text-white bg-black"
                >
                  Redirect to MetaMask
                </button>
                <button
                  onClick={() => setShowMetamaskRedirect(false)}
                  className="px-4 py-2 text-black border border-black"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Connected: show custom dropdown + Wallet Details modal (restored functionality)
  return (
    <>
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          onClick={() => {
            logger.ui(`Wallet dropdown toggled`, {
              showDropdown: !showDropdownMenu,
            });
            setShowDropdownMenu(!showDropdownMenu);
          }}
          className="flex items-center px-2.5 py-0.5 space-x-1 border-2 border-black transition-colors"
        >
          <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-0.5"></div>

          <img src="/svg/metamask-icon.svg" alt="Metamask" />

          <span className="font-medium">{formatAddress(displayAddress)}</span>
        </button>

        {showDropdown && showDropdownMenu && (
          <div className="absolute right-0 z-50 mt-2 w-48 bg-white rounded-lg border border-gray-200 shadow-lg">
            <div className="py-2">
              <button
                onClick={() => {
                  logger.ui(`Wallet details clicked`);
                  setShowDropdownMenu(false);
                  setShowWalletDetails(true);
                  onWalletDetailsClick?.();
                }}
                className="flex items-center px-4 py-2 space-x-3 w-full text-left text-gray-700 transition-colors hover:bg-gray-50"
              >
                <span className="text-lg">👛</span>
                <span className="text-sm">Wallet Details</span>
              </button>

              <button
                onClick={handleDisconnect}
                className="flex items-center px-4 py-2 space-x-3 w-full text-left text-red-600 transition-colors hover:bg-red-50"
              >
                <span className="text-lg">🚪</span>
                <span className="text-sm">Disconnect</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Wallet Details Modal */}
      {showWalletDetails && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
          <div className="p-6 mx-4 w-full max-w-md bg-white rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Wallet Details</h2>
              <button
                onClick={() => {
                  logger.ui(`Wallet details modal closed`);
                  setShowWalletDetails(false);
                }}
                className="text-2xl text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <p className="mb-6 text-gray-600">
              View and manage your connected wallet details.
            </p>

            {/* Wallet QR Code Placeholder */}
            <div className="mb-6">
              <div className="flex justify-center items-center p-4 bg-gray-100 rounded-lg border border-gray-200">
                <div className="flex justify-center items-center w-32 h-32 bg-white rounded-lg">
                  <div className="flex justify-center items-center w-24 h-24 bg-orange-500 rounded-lg">
                    <div className="flex justify-center items-center w-16 h-16 bg-white rounded-lg">
                      <div className="w-8 h-8 bg-orange-500 rounded-lg"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Wallet Information */}
            <div className="mb-6">
              <p className="mb-2 font-medium text-gray-900">Connected Wallet</p>
              <p className="mb-2 text-sm text-gray-600 break-all">
                {displayAddress}
              </p>
              <p className="text-xs text-gray-500">Chain ID: {chainId}</p>
            </div>

            {/* Copy Address Button */}
            <div className="mb-6">
              <button
                onClick={copyAddress}
                className="flex items-center space-x-2 text-gray-600 transition-colors hover:text-gray-800"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                <span className="text-sm">
                  {copied ? "Copied!" : "Copy Address"}
                </span>
              </button>
            </div>

            {/* Disconnect Button */}
            <button
              onClick={handleDisconnect}
              className="py-3 w-full font-medium text-red-500 rounded-lg border border-red-500 transition-colors hover:bg-red-50"
            >
              Disconnect Wallet
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default ThirdwebWalletConnect;
